import { Vector3 } from 'three';
import { SweepMesh } from '../geometry/sweep';
import { jointBasis } from './joints';
import { cylinderMesh } from './supportPrims';
import { PrintSettings, printMmToWorldMetres } from './types';

const PLATE_SEGMENTS = 20;
const BOLT_SEGMENTS = 8;
const HEAD_SEGMENTS = 6;

export interface HssEndplateGeom {
  thickness: number;
  outerRadius: number;
  pitchRadius: number;
  holeRadius: number;
  headRadius: number;
  headHeight: number;
  boltCount: number;
}

/** Resolved flange sizes in world metres, or null when the feature is off / too small. */
export function resolveHssEndplateGeom(
  tubeRadius: number,
  settings: PrintSettings,
  opts?: { tubeSecant?: number; maxThickness?: number; beamSplit?: boolean },
): HssEndplateGeom | null {
  if (!settings.hssEndplateEnabled) return null;
  const tubeR = Math.max(tubeRadius, 1e-4) * Math.max(opts?.tubeSecant ?? 1, 1);
  const split = opts?.beamSplit === true;

  const thicknessMm = split ? settings.hssSplitThicknessMm : settings.hssEndplateThicknessMm;
  const holeMm = split ? settings.hssSplitBoltHoleMm : settings.hssEndplateBoltHoleMm;
  const boltCount = Math.max(
    4,
    Math.round(split ? settings.hssSplitBoltCount : settings.hssEndplateBoltCount),
  );
  const circleT = Math.min(
    1,
    Math.max(0, split ? settings.hssSplitBoltCircle : settings.hssEndplateBoltCircle),
  );

  const holeR = printMmToWorldMetres(holeMm) * 0.5;
  let thickness = printMmToWorldMetres(thicknessMm);
  if (opts?.maxThickness != null) thickness = Math.min(thickness, opts.maxThickness);
  thickness = Math.max(thickness, printMmToWorldMetres(0.15));

  let outerRadius: number;
  const diameterMm = split ? settings.hssSplitDiameterMm : settings.hssEndplateDiameterMm;
  outerRadius = Math.max(
    printMmToWorldMetres(diameterMm * 0.5),
    tubeR + printMmToWorldMetres(0.2),
  );

  const minPitch = tubeR + holeR + printMmToWorldMetres(0.12);
  const maxPitch = outerRadius - holeR - printMmToWorldMetres(0.1);
  const pitchRadius = minPitch + (maxPitch - minPitch) * circleT;
  const holesFit = maxPitch > minPitch + 1e-6 && holeR > printMmToWorldMetres(0.06);
  return {
    thickness,
    outerRadius,
    pitchRadius: holesFit ? pitchRadius : 0,
    holeRadius: holesFit ? holeR : 0,
    headRadius: holesFit ? holeR * 1.65 : 0,
    headHeight: holesFit ? Math.min(thickness * 0.55, holeR * 1.2) : 0,
    boltCount,
  };
}

export function hssBoltOrigins(
  origin: Vector3,
  dir: Vector3,
  geom: HssEndplateGeom,
): Vector3[] {
  if (geom.pitchRadius <= 1e-6 || geom.boltCount < 1) return [];
  const basis = jointBasis(origin, dir);
  const out: Vector3[] = [];
  for (let i = 0; i < geom.boltCount; i++) {
    const ang = (i / geom.boltCount) * Math.PI * 2;
    const u = Math.cos(ang) * geom.pitchRadius;
    const v = Math.sin(ang) * geom.pitchRadius;
    out.push(
      origin
        .clone()
        .addScaledVector(basis.right, u)
        .addScaledVector(basis.up, v),
    );
  }
  return out;
}

export interface HssEndplateMeshes {
  solids: SweepMesh[];
  holes: SweepMesh[];
}

/**
 * Circular blank-flange on one side of a splice. `intoPart` points from the
 * mating face into this piece; the plate sits entirely on this side of `origin`.
 */
export function hssEndplateMeshes(
  origin: Vector3,
  intoPart: Vector3,
  tubeRadius: number,
  settings: PrintSettings,
  opts?: { tubeSecant?: number; maxThickness?: number; boltHeads?: boolean; beamSplit?: boolean },
): HssEndplateMeshes {
  const geom = resolveHssEndplateGeom(tubeRadius, settings, opts);
  if (!geom) return { solids: [], holes: [] };
  const dir = intoPart.clone().normalize();
  const back = origin.clone().addScaledVector(dir, geom.thickness);
  const solids: SweepMesh[] = [
    cylinderMesh(origin, back, geom.outerRadius, geom.outerRadius, { segments: PLATE_SEGMENTS }),
  ];
  const holes: SweepMesh[] = [];
  if (geom.holeRadius > 1e-6) {
    const overshoot = geom.thickness * 0.2 + printMmToWorldMetres(0.08);
    const holeStart = origin.clone().addScaledVector(dir, -overshoot);
    const holeEnd = back.clone().addScaledVector(dir, overshoot);
    for (const bolt of hssBoltOrigins(origin, dir, geom)) {
      const start = bolt.clone().add(holeStart).sub(origin);
      const end = bolt.clone().add(holeEnd).sub(origin);
      holes.push(
        cylinderMesh(start, end, geom.holeRadius, geom.holeRadius, { segments: BOLT_SEGMENTS }),
      );
    }
  }
  if (opts?.boltHeads === true && geom.headHeight > 1e-6 && geom.headRadius > geom.holeRadius) {
    solids.push(...hssBoltHeadMeshes(origin, dir, geom));
  }
  return { solids, holes };
}

/** One bolt head per pitch-circle angle, on the outer (tube) face of this plate. */
export function hssBoltHeadMeshes(
  origin: Vector3,
  intoPart: Vector3,
  geom: HssEndplateGeom,
): SweepMesh[] {
  if (geom.headHeight <= 1e-6 || geom.headRadius <= geom.holeRadius) return [];
  const dir = intoPart.clone().normalize();
  const back = origin.clone().addScaledVector(dir, geom.thickness);
  const headEnd = back.clone().addScaledVector(dir, geom.headHeight);
  const solids: SweepMesh[] = [];
  for (const bolt of hssBoltOrigins(origin, dir, geom)) {
    const hs = bolt.clone().add(back).sub(origin);
    const he = bolt.clone().add(headEnd).sub(origin);
    solids.push(cylinderMesh(hs, he, geom.headRadius, geom.headRadius, { segments: HEAD_SEGMENTS }));
  }
  return solids;
}

/** Horizontal lean correction so a base plate covers the tube footprint. */
export function footerPlateSecant(columnDirY: number): number {
  return 1 / Math.max(Math.abs(columnDirY), 0.35);
}
